export type Locale = "en" | "fr";

export type LocalizedText = {
  readonly en: string;
  readonly fr: string;
};

export type SiteCta = {
  readonly label: LocalizedText;
  readonly shortLabel: LocalizedText;
  readonly destination: string;
  readonly external: boolean;
};

export type ServiceRole = "implementation" | "review";

export type SiteServiceSlug =
  | "ai-verification-review"
  | "production-ai-systems"
  | "data-platforms"
  | "decision-intelligence";

export type ImplementationServiceSlug = Exclude<SiteServiceSlug, "ai-verification-review">;

export type SiteService = {
  readonly slug: SiteServiceSlug;
  readonly role: ServiceRole;
  readonly href: string;
  readonly name: LocalizedText;
  readonly description: LocalizedText;
};

export type IndustryContextKind = "detailed-guide" | "solution-pattern";

export type SiteIndustryContext = {
  readonly slug: string;
  readonly kind: IndustryContextKind;
  readonly href: string;
  readonly label: LocalizedText;
  readonly description: LocalizedText;
  readonly ctaLabel: LocalizedText;
};

export const SITE_URL = "https://vert-ai.com";
export const SITE_NAME = "Vert AI";
export const SITE_LOGO_IMAGE = `${SITE_URL}/vert-logo.png`;
export const SITE_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const BOOKING_URL = "https://calendly.com/d/cvpp-rwv-v5j";
export const CONTACT_EMAIL = "guillaume.belisle@vert-ai.com";
export const FOUNDER_NAME = "Guillaume Belisle";
export const LINKEDIN_URL = "https://www.linkedin.com/in/guillaume-belisle";

export const siteContent = {
  brand: {
    companyName: SITE_NAME,
    categoryStatement: {
      en: "Vert AI builds production AI systems for high-value workflows and recurring decisions.",
      fr: "Vert AI conçoit et déploie des systèmes d’IA en production pour les processus à forte valeur et les décisions récurrentes.",
    },
    shortPositioning: {
      en: "Vert AI builds production AI systems for high-value workflows and recurring decisions.",
      fr: "Vert AI conçoit et déploie des systèmes d’IA en production pour les processus à forte valeur et les décisions récurrentes.",
    },
    longPositioning: {
      en: "We redesign the work, connect the data and systems, define human authority, and deploy the evaluation, monitoring, and controls required for reliable day-to-day use.",
      fr: "Nous repensons le travail, relions les données et les systèmes, définissons l’autorité humaine et mettons en place l’évaluation, la surveillance et les contrôles nécessaires à une utilisation fiable au quotidien.",
    },
    audience: {
      en: "founders, executives, operators, and data leaders responsible for high-value workflows and recurring decisions in operations-led mid-market companies.",
      fr: "fondateurs, membres de la direction, responsables des opérations et responsables des données chargés de processus à forte valeur et de décisions récurrentes dans des entreprises de taille intermédiaire où les opérations sont au cœur de l’activité.",
    },
    geography: {
      en: "Based in Canada, serving Canadian companies first and US companies selectively.",
      fr: "Établie au Canada, Vert AI sert d’abord les entreprises canadiennes et accompagne certaines entreprises américaines.",
    },
    geographyUsageGuidance: {
      en: "Do not over-emphasize location unless useful for SEO/contact.",
      fr: "Ne pas surexposer la localisation sauf lorsque c’est utile pour le SEO ou le contact.",
    },
  },
  ctas: {
    verificationReview: {
      label: {
        en: "Apply for the AI Verification Review",
        fr: "Présenter une demande pour l’examen de vérification de l’IA",
      },
      shortLabel: {
        en: "Apply for AI Review",
        fr: "Demander un examen de l’IA",
      },
      destination: "/apply/ai-verification-review",
      external: false,
    },
    reviewOffer: {
      label: {
        en: "Review a Live AI Initiative",
        fr: "Faire examiner une initiative d’IA en exploitation",
      },
      shortLabel: {
        en: "Review a Live AI Initiative",
        fr: "Faire examiner une initiative d’IA",
      },
      destination: "/services/ai-verification-review",
      external: false,
    },
    productionSystems: {
      label: {
        en: "Explore Production AI Systems",
        fr: "Explorer les systèmes d’IA en production",
      },
      shortLabel: {
        en: "Production AI Systems",
        fr: "Systèmes d’IA en production",
      },
      destination: "/services/production-ai-systems",
      external: false,
    },
    primary: {
      label: {
        en: "Discuss a production AI opportunity",
        fr: "Discuter d’un projet d’IA en production",
      },
      shortLabel: {
        en: "Discuss a production AI opportunity",
        fr: "Discuter d’un projet d’IA en production",
      },
      destination: "/contact",
      external: false,
    },
    secondary: {
      label: {
        en: "Assess your AI readiness",
        fr: "Évaluer votre préparation à l’IA",
      },
      shortLabel: {
        en: "Assess your AI readiness",
        fr: "Évaluer votre préparation à l’IA",
      },
      destination: "/ai-readiness-scorecard",
      external: false,
    },
    proof: {
      label: {
        en: "Review real engagements",
        fr: "Voir des mandats réels",
      },
      shortLabel: {
        en: "Review real engagements",
        fr: "Voir des mandats réels",
      },
      destination: "/work",
      external: false,
    },
  },
  messagePillars: [
    {
      en: "Choose the right workflow before building.",
      fr: "Choisir le bon processus opérationnel avant de construire.",
    },
    {
      en: "Define risk, ownership, human review, and escalation before automating.",
      fr: "Définir le risque, la responsabilité, la validation humaine et le transfert à un niveau supérieur avant d’automatiser.",
    },
    {
      en: "Fix the data foundation required for useful AI.",
      fr: "Mettre en place les fondations de données nécessaires à un système fiable.",
    },
    {
      en: "Build an operating system, not an isolated demo.",
      fr: "Construire un système exploitable, pas une démonstration isolée.",
    },
    {
      en: "Measure business value and adoption.",
      fr: "Mesurer la valeur opérationnelle et l’adoption.",
    },
    {
      en: "Operate with evaluation, monitoring, human review, and governance.",
      fr: "Exploiter avec évaluation, surveillance, validation humaine et gouvernance.",
    },
  ],
  serviceCatalogue: [
    {
      slug: "production-ai-systems",
      role: "implementation",
      href: "/services/production-ai-systems",
      name: {
        en: "Production AI Systems",
        fr: "Systèmes d’IA en production",
      },
      description: {
        en: "When the workflow is known: redesign it, automate the right steps, integrate the system, and operate it with evidence and control.",
        fr: "Lorsque le processus est connu : le repenser, automatiser les bonnes étapes, intégrer le système et l’exploiter avec des preuves et des contrôles.",
      },
    },
    {
      slug: "data-platforms",
      role: "implementation",
      href: "/services/data-platforms",
      name: {
        en: "Data Platforms for AI",
        fr: "Plateformes de données pour l’IA",
      },
      description: {
        en: "When unreliable data blocks action: build the smallest governed data path the valuable workflow actually needs.",
        fr: "Lorsque des données peu fiables bloquent l’action : construire le plus petit parcours de données gouverné dont le processus prioritaire a réellement besoin.",
      },
    },
    {
      slug: "decision-intelligence",
      role: "implementation",
      href: "/services/decision-intelligence",
      name: {
        en: "Decision Intelligence",
        fr: "Intelligence décisionnelle",
      },
      description: {
        en: "When recurring decisions stay slow or inconsistent: connect signals, policy, recommendations, action, and outcome feedback.",
        fr: "Lorsque les décisions récurrentes restent lentes ou incohérentes : relier les signaux, les règles, les recommandations, l’action et la rétroaction sur les résultats.",
      },
    },
    {
      slug: "ai-verification-review",
      role: "review",
      href: "/services/ai-verification-review",
      name: {
        en: "AI Verification & Production Readiness Review",
        fr: "Examen de vérification de l’IA et de préparation à la production",
      },
      description: {
        en: "A specialized review for one live or decision-ready AI initiative when leadership must fund, launch, expand, pause, or stop it based on evidence, controls, ownership, and economics.",
        fr: "Un examen spécialisé d’une initiative d’IA en exploitation ou prête à faire l’objet d’une décision, lorsque la direction doit décider de la financer, de la lancer, de l’élargir, de la suspendre ou de l’arrêter selon les preuves, les contrôles, les responsabilités et la viabilité économique.",
      },
    },
  ] satisfies SiteService[],
  industries: [
    {
      slug: "retail-ecommerce",
      kind: "detailed-guide",
      href: "/industries/retail-ecommerce-ai",
      label: {
        en: "Retail & Ecommerce",
        fr: "Commerce de détail et commerce électronique",
      },
      description: {
        en: "Inventory, merchandising, fulfillment, returns, and customer exceptions where data must lead to governed action.",
        fr: "Inventaire, marchandisage, traitement des commandes, retours et exceptions touchant la clientèle qui exigent de transformer les données en actions gouvernées.",
      },
      ctaLabel: {
        en: "Review workflows and fit",
        fr: "Voir les processus et l’adéquation",
      },
    },
    {
      slug: "cpg",
      kind: "detailed-guide",
      href: "/industries/cpg-ai",
      label: {
        en: "Consumer Goods",
        fr: "Biens de consommation",
      },
      description: {
        en: "Planning, demand, product, customer, and supply-chain decisions across fragmented commercial and operational data.",
        fr: "Planification de la demande, décisions sur les produits et la clientèle, et coordination de la chaîne d’approvisionnement à partir de données commerciales et opérationnelles fragmentées.",
      },
      ctaLabel: {
        en: "Review workflows and fit",
        fr: "Voir les processus et l’adéquation",
      },
    },
    {
      slug: "franchise-multi-location",
      kind: "detailed-guide",
      href: "/industries/franchise-multi-location-ai",
      label: {
        en: "Franchise & Multi-location Operations",
        fr: "Franchises et réseaux multisites",
      },
      description: {
        en: "Location-level performance, staffing, standards, and exception follow-up across distributed operating networks.",
        fr: "Performance par établissement, dotation, respect des normes et suivi des exceptions dans des réseaux d’exploitation distribués.",
      },
      ctaLabel: {
        en: "Review workflows and fit",
        fr: "Voir les processus et l’adéquation",
      },
    },
    {
      slug: "manufacturing-distribution",
      kind: "solution-pattern",
      href: "/solutions/predictive-models-decision-science",
      label: {
        en: "Manufacturing & Distribution",
        fr: "Fabrication et distribution",
      },
      description: {
        en: "Planning, demand, quality, maintenance, and distribution decisions where forecasts and scores must become governed action.",
        fr: "Planification de la demande, décisions de qualité et de maintenance, et exécution de la distribution lorsque les prévisions et les cotes doivent mener à une action gouvernée.",
      },
      ctaLabel: {
        en: "Explore predictive decision systems",
        fr: "Explorer les systèmes décisionnels prédictifs",
      },
    },
    {
      slug: "professional-b2b-services",
      kind: "solution-pattern",
      href: "/solutions/document-intelligence",
      label: {
        en: "Professional & B2B Services",
        fr: "Services professionnels et interentreprises",
      },
      description: {
        en: "Document intake, extraction, validation, and exception routing where traceability and human review are essential.",
        fr: "Réception, extraction et validation de documents, avec acheminement des exceptions lorsque la traçabilité et la validation humaine sont essentielles.",
      },
      ctaLabel: {
        en: "Explore document intelligence",
        fr: "Explorer l’intelligence documentaire",
      },
    },
  ] satisfies SiteIndustryContext[],
  proof: {
    founderLedDelivery: {
      en: "Founder-led scoping, architecture, and implementation with senior technical involvement close to production decisions.",
      fr: "Cadrage et décisions techniques clés dirigés par le fondateur, avec des spécialistes chevronnés mobilisés lorsque le mandat l’exige.",
    },
    productionExperience: {
      en: "Production AI, ML, data platform, decision-system, and automation experience applied from strategy through build.",
      fr: "Expérience en IA en production, apprentissage automatique, plateformes de données, systèmes décisionnels et automatisation, du cadrage à l’exploitation.",
    },
    selectedOperatorExperience: {
      en: "Founder experience includes designing and shipping production AI, ML, and data systems in operator environments including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware. These organizations are not presented as Vert AI clients.",
      fr: "L’expérience professionnelle du fondateur comprend la conception et la livraison de systèmes d’IA, d’apprentissage automatique et de données en production dans des environnements comme Louis Vuitton, Canada Goose, Lowe’s et Home Hardware. Ces entreprises ne sont pas présentées comme des clientes de Vert AI.",
    },
  },
  trustStandards: [
    {
      en: "Evaluation harnesses",
      fr: "Grilles et bancs d’évaluation",
    },
    {
      en: "Prompt and workflow standards",
      fr: "Normes pour les instructions et les processus",
    },
    {
      en: "Human-in-the-loop controls",
      fr: "Contrôles de validation humaine",
    },
    {
      en: "Escalation logic",
      fr: "Règles de transfert",
    },
    {
      en: "Observability and monitoring",
      fr: "Observabilité et surveillance",
    },
    {
      en: "Privacy and security review",
      fr: "Examen de la confidentialité et de la sécurité",
    },
    {
      en: "Data quality checks",
      fr: "Contrôles de qualité des données",
    },
    {
      en: "Ownership and governance",
      fr: "Responsabilité et gouvernance",
    },
    {
      en: "ROI and adoption measurement",
      fr: "Mesure du rendement et de l’adoption",
    },
  ],
  seo: {
    defaultTitle: "Production AI Systems for High-Value Workflows | Vert AI",
    defaultDescription:
      "Vert AI builds production AI systems for high-value workflows and recurring decisions, with reliable data, human controls, evaluation, monitoring, and measurement.",
    defaultImage: SITE_OG_IMAGE,
    defaultImageAlt: "Vert AI production AI systems",
    defaultImageAltFr: "Systèmes d’IA en production de Vert AI",
  },
} as const;

export const serviceCatalogue = siteContent.serviceCatalogue;
export const reviewService = serviceCatalogue.find((service) => service.role === "review")!;
export const implementationServices = serviceCatalogue.filter((service) => service.role === "implementation");
export const detailedIndustryGuides = siteContent.industries.filter(
  (industry) => industry.kind === "detailed-guide",
);
export const adjacentIndustryContexts = siteContent.industries.filter(
  (industry) => industry.kind === "solution-pattern",
);

export function localize(text: LocalizedText, locale: Locale = "en") {
  return text[locale] || text.en;
}

export function getCtaLabel(
  cta: SiteCta,
  locale: Locale = "en",
  variant: "label" | "shortLabel" = "label",
) {
  return localize(cta[variant], locale);
}
